import type { Funnel } from "@fbs/funnel-schema";

/**
 * Der Torwächter für das geladene Funnel-Dokument.
 *
 * **Warum hier nicht das Zod-Schema läuft.** Es wäre die naheliegende Lösung
 * und die falsche: Zod macht zwei Drittel des Bündels aus, das jeder Besucher
 * am Handy lädt, und es prüft etwas, das längst geprüft ist. Ein Dokument
 * kommt nur über den Veröffentlichungsweg in die Datenbank, dort läuft
 * funnelLesen und funnelPruefen, und eine veröffentlichte Version ist danach
 * per Datenbank-Trigger unveränderlich.
 *
 * **Warum trotzdem geprüft wird.** Zwischen dem Veröffentlichen und diesem
 * Browser liegen eine Datenbankspalte, ein Zwischenspeicher und eine Leitung.
 * Kommt hier etwas Unerwartetes an, soll der Besucher eine Meldung sehen und
 * keine weiße Seite. Geprüft wird deshalb genau das, worauf sich die Engine
 * verlässt, nicht jedes Feld des Schemas.
 *
 * **Was das voraussetzt.** Dass die Vorgabewerte im gespeicherten Dokument
 * stehen. Genau das tut funnel-import.ts: Es speichert das Ergebnis von
 * funnelLesen, nicht die Eingabedatei. Diese Zusage ist der Grund, warum die
 * Komponenten `settings.navigation.allow_back` direkt lesen dürfen, statt
 * überall `?? true` zu schreiben.
 */

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert);
}

function hatFelder(wert: unknown, felder: string[]): boolean {
  if (!istObjekt(wert)) return false;
  return felder.every((feld) => wert[feld] !== undefined);
}

/**
 * Prüft das Dokument auf das, was die Engine braucht, und gibt es typisiert
 * zurück. `null` bedeutet: nicht benutzbar, dem Besucher die Meldung zeigen.
 */
export function dokumentLesen(roh: unknown): Funnel | null {
  if (!istObjekt(roh)) return null;
  if (roh["schema_version"] !== 1) return null;
  if (typeof roh["id"] !== "string" || roh["id"] === "") return null;

  const schritte = roh["steps"];
  if (!Array.isArray(schritte) || schritte.length === 0) return null;

  for (const schritt of schritte) {
    if (!istObjekt(schritt)) return null;
    if (typeof schritt["id"] !== "string" || schritt["id"] === "") return null;
    if (typeof schritt["type"] !== "string") return null;

    // Auswahlschritte ohne Optionen wären ein Bildschirm ohne Ausweg.
    if (schritt["type"] === "single_choice" || schritt["type"] === "multiple_choice") {
      const optionen = schritt["options"];
      if (!Array.isArray(optionen) || optionen.length === 0) return null;
    }
    if (schritt["type"] === "contact") {
      const felder = schritt["fields"];
      if (!Array.isArray(felder) || felder.length === 0) return null;
    }
    if (schritt["type"] === "consent") {
      const eintraege = schritt["items"];
      if (!Array.isArray(eintraege) || eintraege.length === 0) return null;
    }
  }

  // Die Vorgabewerte, auf die sich Hülle und Komponenten verlassen.
  const einstellungen = roh["settings"];
  if (!istObjekt(einstellungen)) return null;
  if (!hatFelder(einstellungen["progress"], ["show"])) return null;
  if (
    !hatFelder(einstellungen["navigation"], [
      "allow_back",
      "auto_advance_single_choice",
      "auto_advance_delay_ms",
    ])
  ) {
    return null;
  }
  if (!hatFelder(einstellungen["persistence"], ["resume_enabled", "resume_ttl_hours"])) {
    return null;
  }
  if (!hatFelder(einstellungen["spam"], ["honeypot"])) return null;

  /*
   * Aeltere Fassungen kennen "absenden" nicht.
   *
   * Die Einstellung kam mit der Partnerseite dazu. Ein Dokument, das vorher
   * veroeffentlicht wurde, hat sie nicht - und weil diese Funktion das
   * Ergebnis als `Funnel` ausgibt, behauptet der Typ danach ein Feld, das es
   * nicht gibt. Wer es liest, bekommt einen TypeError statt eines Wertes.
   *
   * Ergaenzt statt abgelehnt: Ein Quiz, das seit Wochen laeuft, darf nicht
   * aufhoeren zu funktionieren, weil eine Einstellung hinzugekommen ist.
   */
  if (!istObjekt(einstellungen["absenden"])) {
    einstellungen["absenden"] = { ziel: "lead" };
  }

  // Dasselbe für die Vertrauenspunkte, seit dem 15.09.2026.
  if (!Array.isArray(einstellungen["trust_points"])) {
    einstellungen["trust_points"] = [];
  }

  if (
    !hatFelder(roh["theme"], ["primary", "primary_contrast", "surface", "text", "radius"])
  ) {
    return null;
  }

  const abschluss = roh["completion"];
  if (!istObjekt(abschluss)) return null;
  if (typeof abschluss["headline"] !== "string" || abschluss["headline"] === "")
    return null;
  if (!hatFelder(abschluss["loader"], ["enabled", "duration_ms", "messages"]))
    return null;
  if (!Array.isArray(abschluss["next_steps"])) return null;

  return roh as unknown as Funnel;
}
